import { useRef, type KeyboardEvent, type ReactNode } from 'react'
import {
  CHROME_BUTTON_CLASS,
  CHROME_CHECKBOX_CLASS,
  CHROME_FOCUS_CLASS,
  CHROME_POPOVER_CLASS,
  CHROME_RADIO_CLASS,
  CHROME_SHEET_DONE_CLASS,
  CHROME_SHEET_TITLE_CLASS,
  FILTERS_PANEL_ID,
  chromeButtonLabelClass,
} from '../../lib/chrome_config'
import {
  CONFIDENCE_ANY_LABEL,
  CONFIDENCE_STEPS,
  INDEPENDENCE_STATUSES,
  INDEPENDENCE_STATUS_LABELS,
  SIZE_CLASSES,
  SIZE_CLASS_LABELS,
  WINERY_FILTER_COVERAGE,
  WINERY_FILTER_PHASES,
  WINERY_UNKNOWN,
  WINERY_VISITING_LABELS,
  WINERY_VISITING_OPTIONS,
  WINERY_VISITING_SPECIAL,
} from '../../lib/map_config'
import { EMPTY_WINERY_FILTERS, activeFilterCount } from '../../lib/winery_viewport'
import type { WineryFacets, WineryFilters as WineryFilterState } from '../../types/atlas'

interface WineryFiltersProps {
  facets: WineryFacets
  filters: WineryFilterState
  open: boolean
  onToggleOpen: (open: boolean) => void
  onChange: (filters: WineryFilterState) => void
  /** A detail panel is open beside the bar, which leaves the bar less room for words. */
  beside?: boolean
}

// An explicit locale, not the viewer's: the SSR bundle formats with the server's
// and a disagreement between the two is a hydration mismatch.
const number = new Intl.NumberFormat('en-US')

// Why a bucket is empty, not merely that it is. A control that says "0" and
// nothing else reads as a broken filter; one that says nothing of the kind has
// been recorded yet reads as an atlas that knows its own edges (KTD11). The
// sentences are in map_config.ts, written for a reader of the atlas.
const reasonFor = (key: string) => WINERY_FILTER_PHASES[key] ?? 'None recorded yet.'

// The other half of that rule, for a control that now SELECTS. An enabled
// control needs no reason, but after P6b the independence data is lopsided
// rather than merely incomplete — French and Italian charters, a US permit
// prior, nothing else — and `independent 7,312` says nothing about where those
// rows are. So an enabled control carries a coverage note where a disabled one
// carried a reason: same affordance, opposite state.
const coverageFor = (key: string): string | undefined => WINERY_FILTER_COVERAGE[key]

interface Bucket {
  value: string
  count: number
}

// `unknown` is a value, not a gap, so it is always in the list. It moves to the
// FRONT when it is the largest bucket, which in P6a it always is: the shape of
// what is known should be the first thing read rather than something to be
// inferred from a list that buries it last.
function buckets(vocabulary: string[], counts: Record<string, number>): Bucket[] {
  const known = vocabulary.map((value) => ({ value, count: counts[value] ?? 0 }))
  const unknown = { value: WINERY_UNKNOWN, count: counts[WINERY_UNKNOWN] ?? 0 }

  return known.every((bucket) => unknown.count >= bucket.count) ? [unknown, ...known] : [...known, unknown]
}

const toggled = (selected: string[], value: string, on: boolean) =>
  on ? [...selected, value] : selected.filter((entry) => entry !== value)

// The journal's section heading, on a real `<legend>`: the five names are what
// a screen reader announces each group by and what the system test reads.
const LEGEND_CLASS = 'font-display text-[17px] font-semibold'

// One row is one target: 44px under a thumb, 36px under a pointer, the whole
// width of the surface. `group` so the count can follow the row into its
// disabled grey, and `has-[:disabled]` so the grey comes from the input's own
// state rather than from a class that has to be kept in step with it.
//
// A disabled row's words are the soft ink at full strength, not at 70% of it.
// They are the words a reader needs in order to know WHY the row is off, and
// at 70% they measured 3.2 to 1 on the paper; the greyed box says "disabled".
const ROW_BASE_CLASS =
  'group -mx-2 flex gap-2.5 rounded-lg px-2 text-sm hover:bg-black/[0.03] ' +
  'has-[:disabled]:text-ink-soft has-[:disabled]:hover:bg-transparent'
const ROW_CLASS = `${ROW_BASE_CLASS} min-h-11 items-center md:min-h-9`

// A confidence step holds three lines, so it is top-aligned and padded rather
// than centred at a fixed height: a radio floated to the middle of three lines
// points at none of them.
const STEP_ROW_CLASS = `${ROW_BASE_CLASS} items-start py-2`

// Counts are right-aligned and tabular, so they read down the surface as one
// column of figures.
const COUNT_CLASS = 'text-xs tabular-nums text-ink-soft group-has-[:disabled]:text-inherit'

const NOTE_CLASS = 'text-xs leading-relaxed text-ink-soft'

/**
 * R31's filters, presented so that an atlas which knows a great deal about
 * two countries and almost nothing about the rest reads as honest rather than as
 * broken.
 *
 * Every control carries its facet count from the WHOLE live atlas, never the
 * viewport — the panel answers "what does the atlas know", and a count that
 * moved with the camera would read as a second map disagreeing with the first.
 * A bucket with no rows is **disabled with its reason**, exactly as
 * `LayerToggles` disables the region toggle below its threshold.
 * Enabled-but-inert is the one thing this must never be.
 *
 * **WHAT THE ATLAS HOLDS COMES FIRST, AND WHAT IT DOES NOT IS ONE LINE.** The
 * groups were in the order they were built — size, independence,
 * certifications, visiting, confidence, direct sales — each with every control
 * drawn, so against today's atlas the surface opened on four greyed size
 * classes and their paragraph, was 1,700 px of content in a 770 px column, and
 * kept the one filter the data supports best, how to visit, fourth. Now a
 * group with something behind it is drawn in the open, in the order a reader
 * plans a visit by — how to get in, who owns it, how it farms, how big, how
 * sure — and the groups with NOTHING behind them are folded into one
 * disclosure at the foot that names them ("Not recorded yet: size,
 * certifications, direct sales"). Inside it they are what they always were:
 * every control present, disabled, with its reason. KTD11's rule is kept to
 * the letter — nothing is enabled and inert, nothing is silently absent — and
 * a reader is not made to scroll past nine switches that do nothing.
 *
 * **And a bucket WITH rows carries a coverage note where the empty one carried a
 * reason.** P6b filled the independence axis with ~7,300 rows that are almost
 * all French and Italian, so `independent 7,312` beside an empty California is a
 * number a reader cannot interpret: it says nothing about whether there are no
 * independent producers there or no evidence yet. Same affordance, opposite
 * state — and the same move the map already makes with
 * `UNCOVERED_COUNTRY_FILL_COLOR`. The note is a paragraph, so it sits behind
 * "What this covers" rather than under every row it applies to; it is still
 * the control's description for anything that reads one.
 *
 * **One deliberate asymmetry, pinned by one test so it cannot drift to whichever
 * a later edit finds tidier.** Direct sales is rendered and DISABLED, because
 * `winery_dtc_channels` is a real table nothing fills until P6c. Organic and
 * biodynamic are rendered and ENABLED the moment their buckets hold rows,
 * because P6b created those columns and filled them — the same rule, in its
 * other state. (They were absent entirely in P6a, on the stronger rule that a
 * filter over a column which does not exist is not a disabled filter.)
 *
 * **Minimum confidence is three named steps** (`CONFIDENCE_STEPS` in
 * map_config.ts has why they are three, and words). Each step's count is the
 * AT-OR-ABOVE count, per axis — the reading a threshold needs — because the
 * threshold gates ownership and size separately, each with its own
 * confidence. The certification controls are outside the threshold entirely:
 * a certification carries no confidence to gate.
 *
 * **Collapsed by default at every width, and a popover when open.** It used to
 * be a box in the header's left column that grew in place, with no width of its
 * own. On a desktop each empty bucket's reason is a sentence about 1,075px
 * long, so the open box took the column's max-content width, the column took
 * the whole row, and the header's `flex-wrap` sent Journal / Wishlist / Sources
 * to a second line that began below a viewport-tall column — the nav at y≈896
 * on a 900px screen. The surface now HAS a width (22rem from `md`) and sits out
 * of the header's flow: a popover under the bar from `md`, a bottom sheet on a
 * phone, where the bar stays reachable above it to close it again. The
 * active-filter count on the button is what stops a filter from being invisible
 * while collapsed.
 *
 * **Unmounted while closed** — the opposite of `LayerToggles`, and also
 * deliberate: nothing outside refers into it, so there is no description to
 * keep resolvable, and a closed filter panel that is simply not there cannot be
 * tabbed into by accident.
 *
 * **The title and the footer are flex siblings of the scrolling body, not
 * `sticky` inside it.** A sticky footer can sit on top of the checkbox a
 * reader has just scrolled to, which is a control that looks reachable and is
 * not; and the title is what a sheet is called, so it does not scroll away —
 * it used to have none, and on a phone its first line slid under the sheet's
 * rounded corner. Neither is focusable ahead of the first control, which keeps
 * the first tab stop the first filter.
 */
export default function WineryFilters({
  facets,
  filters,
  open,
  onToggleOpen,
  onChange,
  beside = false,
}: WineryFiltersProps) {
  const active = activeFilterCount(filters)
  const button = useRef<HTMLButtonElement>(null)

  // Closing from inside hands focus back to the button: the surface is about to
  // unmount, and focus left on a removed node falls to `<body>`, which on this
  // page is a dead end above a canvas.
  const close = () => {
    onToggleOpen(false)
    button.current?.focus()
  }

  // Escape closes the surface when focus is inside it or on its button, and
  // only then. The detail panel listens for Escape on `document`, so the key is
  // stopped here: a reader putting the filters away has not asked to be
  // navigated off the winery they are reading.
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || !open) return

    event.stopPropagation()
    close()
  }

  // The steps the control offers, with what the server counted at each. A
  // threshold the server did not send is one it does not answer, and is not
  // offered.
  const steps = CONFIDENCE_STEPS.flatMap((step) => {
    const counted = facets.confidence.find((entry) => entry.threshold === step.threshold)

    return counted ? [{ ...step, size: counted.size, independence: counted.independence }] : []
  })

  // EVERY GROUP, IN THE ORDER A READER PLANS A VISIT BY, each with whether the
  // atlas holds anything for it. `unknown` does not count as holding
  // something: a group whose only selectable row is "nobody knows" is a group
  // with nothing recorded.
  const groups: { id: string; name: string; live: boolean; body: ReactNode }[] = [
    // Only when the server sent the bucket, which it always does now; a facets
    // fixture from before `visiting` existed renders as before.
    ...(facets.visiting
      ? [
          {
            id: 'visiting',
            name: 'visiting',
            live: WINERY_VISITING_OPTIONS.some((value) => (facets.visiting?.[value] ?? 0) > 0),
            body: (
              <VisitingFilter
                key="visiting"
                counts={facets.visiting}
                selected={filters.visiting ?? []}
                onChange={(visiting) => onChange({ ...filters, visiting })}
              />
            ),
          },
        ]
      : []),
    {
      id: 'independence',
      name: 'ownership',
      live: INDEPENDENCE_STATUSES.some((value) => (facets.independence_statuses[value] ?? 0) > 0),
      body: (
        <fieldset key="independence">
          <legend className={LEGEND_CLASS}>Independence</legend>
          <Choices
            name="winery-independence"
            options={buckets(INDEPENDENCE_STATUSES, facets.independence_statuses).map((bucket) => ({
              value: bucket.value,
              label: INDEPENDENCE_STATUS_LABELS[bucket.value] ?? bucket.value,
              count: bucket.count,
              checked: filters.independence_statuses.includes(bucket.value),
              onChange: (on) =>
                onChange({
                  ...filters,
                  independence_statuses: toggled(filters.independence_statuses, bucket.value, on),
                }),
            }))}
          />
        </fieldset>
      ),
    },
    {
      id: 'certifications',
      name: 'certifications',
      live: facets.organic.certified > 0 || facets.biodynamic.certified > 0,
      // R31's last clause, which P6a deliberately did not build: the column
      // did not exist then, so the control was ABSENT rather than disabled.
      // P6b creates it and fills it, so the control is rendered and, where
      // there are rows, enabled — P6a's own rule, applied to the state it was
      // written for.
      //
      // Two checkboxes rather than one three-state control, because a producer
      // can hold both certifications and the two columns are filtered
      // independently.
      body: (
        <fieldset key="certifications">
          <legend className={LEGEND_CLASS}>Certifications</legend>
          <Choices
            name="winery-certifications"
            options={[
              {
                value: 'organic',
                label: 'Certified organic',
                count: facets.organic.certified,
                checked: filters.organic,
                onChange: (on) => onChange({ ...filters, organic: on }),
              },
              {
                value: 'biodynamic',
                label: 'Certified biodynamic',
                count: facets.biodynamic.certified,
                checked: filters.biodynamic,
                onChange: (on) => onChange({ ...filters, biodynamic: on }),
              },
            ]}
          />
          {/* Said out loud because the threshold control is on the same surface
              and a filter that visibly ignores its neighbour reads as a bug.
              `winery_certifications` has no confidence column at all: a badge
              is a documentary fact the publisher states, not an inference over
              weak signals, so there is nothing here to gate (U3). */}
          <p data-testid="winery-certifications-note" className={`${NOTE_CLASS} ml-[26px] mt-1`}>
            A certification is published by the producer and carries no confidence, so the minimum confidence does
            not apply to these two.
          </p>
        </fieldset>
      ),
    },
    {
      id: 'size',
      name: 'size',
      live: SIZE_CLASSES.some((value) => (facets.size_classes[value] ?? 0) > 0),
      body: (
        <fieldset key="size">
          <legend className={LEGEND_CLASS}>Size</legend>
          <Choices
            name="winery-size-class"
            options={buckets(SIZE_CLASSES, facets.size_classes).map((bucket) => ({
              value: bucket.value,
              label: SIZE_CLASS_LABELS[bucket.value] ?? bucket.value,
              count: bucket.count,
              checked: filters.size_classes.includes(bucket.value),
              onChange: (on) => onChange({ ...filters, size_classes: toggled(filters.size_classes, bucket.value, on) }),
            }))}
          />
        </fieldset>
      ),
    },
    {
      id: 'confidence',
      name: 'confidence',
      live: steps.some((step) => step.size > 0 || step.independence > 0),
      body: (
        <fieldset key="confidence" data-testid="winery-confidence">
          <legend className={LEGEND_CLASS}>Minimum confidence</legend>

          <label className={`${ROW_CLASS} mt-1`}>
            <input
              type="radio"
              name="winery-confidence"
              className={CHROME_RADIO_CLASS}
              checked={filters.min_confidence === null}
              onChange={() => onChange({ ...filters, min_confidence: null })}
            />
            <span className="flex-1">{CONFIDENCE_ANY_LABEL}</span>{' '}
            <span className={COUNT_CLASS}>{number.format(facets.total)}</span>
          </label>

          {steps.map((step) => {
            // A step is empty only when NEITHER axis has anything at or above
            // it: the threshold gates size and ownership separately, so a step
            // that still admits an ownership claim is not an inert one.
            const empty = step.size === 0 && step.independence === 0

            return (
              <div key={step.threshold}>
                <label className={STEP_ROW_CLASS}>
                  <input
                    type="radio"
                    name="winery-confidence"
                    className={`${CHROME_RADIO_CLASS} mt-0.5`}
                    disabled={empty}
                    checked={filters.min_confidence === step.threshold}
                    aria-describedby={empty ? `confidence-${step.threshold}-reason` : undefined}
                    onChange={() => onChange({ ...filters, min_confidence: step.threshold })}
                  />
                  {/* The name of the step, what it means in a clause, and then
                      what the atlas holds at or above it ON EACH AXIS — two
                      counts, because the threshold has always gated ownership
                      and size each with its own confidence, and one number
                      would be a count of neither. */}
                  <span className="min-w-0 flex-1">
                    <span className="block">
                      {step.label} <span className="text-ink-soft group-has-[:disabled]:text-inherit">— {step.says}</span>
                    </span>
                    <span className={`block ${COUNT_CLASS}`}>
                      {number.format(step.independence)} by ownership · {number.format(step.size)} by size
                    </span>
                  </span>
                </label>
                {empty && (
                  <p id={`confidence-${step.threshold}-reason`} className={`${NOTE_CLASS} mb-1 ml-[26px]`}>
                    {reasonFor('confidence')}
                  </p>
                )}
              </div>
            )
          })}
        </fieldset>
      ),
    },
    {
      id: 'direct-sales',
      name: 'direct sales',
      live: facets.direct_sales.available > 0,
      body: (
        <fieldset key="direct-sales">
          <legend className={LEGEND_CLASS}>Direct sales</legend>
          <Choices
            name="winery-direct-sales"
            options={[
              {
                value: 'direct_sales',
                label: 'Sells direct to the public',
                count: facets.direct_sales.available,
                checked: filters.direct_sales,
                onChange: (on) => onChange({ ...filters, direct_sales: on }),
              },
            ]}
          />
        </fieldset>
      ),
    },
    // A quoted club, from the field research ledger; absent on a facets
    // fixture from before the bucket existed.
    ...(facets.wine_club
      ? [
          {
            id: 'wine-club',
            name: 'wine club',
            live: facets.wine_club.yes > 0,
            body: (
              <fieldset key="wine-club" data-testid="winery-wine-club">
                <legend className={LEGEND_CLASS}>Wine club</legend>
                <Choices
                  name="winery-wine-club"
                  options={[
                    {
                      value: 'wine_club',
                      label: 'Has a wine club',
                      count: facets.wine_club.yes,
                      checked: filters.wine_club ?? false,
                      onChange: (on) => onChange({ ...filters, wine_club: on }),
                    },
                  ]}
                />
              </fieldset>
            ),
          },
        ]
      : []),
  ]

  const live = groups.filter((group) => group.live)
  const empty = groups.filter((group) => !group.live)

  return (
    <>
      <button
        ref={button}
        type="button"
        aria-expanded={open}
        aria-controls={FILTERS_PANEL_ID}
        title="Filters"
        onClick={() => onToggleOpen(!open)}
        onKeyDown={onKeyDown}
        // `min-w-11` rather than the Layers button's fixed width: this one grows
        // to show its count, which is the only thing that says a filter is on
        // once the surface is shut.
        className={`${CHROME_BUTTON_CLASS} min-w-11 px-3`}
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        >
          <path d="M3 5h14M6 10h8M8.5 15h3" />
        </svg>
        <span className={chromeButtonLabelClass(beside)}>Filters</span>
        {active > 0 && (
          <span
            data-testid="winery-filters-count"
            className="rounded-full bg-accent px-2 py-0.5 text-[11px] font-semibold tabular-nums text-white in-aria-expanded:bg-white in-aria-expanded:text-accent"
          >
            {active} active
          </span>
        )}
      </button>

      {open && (
        <div onKeyDown={onKeyDown} className={`${CHROME_POPOVER_CLASS} flex flex-col overflow-hidden`}>
          {/* What the sheet is called, as every other sheet from this edge
              says (`CHROME_SHEET_TITLE_CLASS`). */}
          <h2 className={`${CHROME_SHEET_TITLE_CLASS} px-4 pt-4`}>Filters</h2>

          <div
            id={FILTERS_PANEL_ID}
            data-testid={FILTERS_PANEL_ID}
            className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-4 pb-4 pt-2"
          >
            {/* The facets count the WHOLE live atlas, which is right and stays
                (KTD11) — but FIVI publishes no coordinates at all, so after P6b
                roughly 1,853 of the `independent` rows have no dot. A bucket a
                quarter larger than anything the map can draw has to say so here;
                counting only drawable rows instead would answer a different
                question than this panel asks, and would tell a reader filtering
                for Italian independents that there are none.

                With every producer located the short form of the same rule
                still stands at the top: a count that does not move when the
                camera does has to say that it was never going to. */}
            {facets.unlocated > 0 ? (
              <p data-testid="winery-facets-unlocated" className={NOTE_CLASS}>
                Counts are the whole atlas: {number.format(facets.unlocated)} of {number.format(facets.total)}{' '}
                producers have no coordinates from any source, so they are counted here but not drawn on the map.
              </p>
            ) : (
              <p className={NOTE_CLASS}>Counts are the whole atlas, not what is on screen.</p>
            )}

            {live.map((group) => group.body)}

            {/* What the atlas holds nothing for yet: one line that names it,
                opening to the same controls, each disabled with its reason.
                A native disclosure, so it opens to a tap, a click or a key
                with no state of ours, and what is inside it is in the
                document — and described — whether or not it is open. */}
            {empty.length > 0 && (
              <details data-testid="winery-filters-empty" className="group/empty border-t border-black/5 pt-3">
                <summary
                  className={`-mx-2 flex min-h-11 cursor-pointer list-none items-center gap-2 rounded-lg px-2 text-[13px] text-ink-soft hover:bg-black/[0.03] md:min-h-9 [&::-webkit-details-marker]:hidden ${CHROME_FOCUS_CLASS}`}
                >
                  <span className="flex-1">Not recorded yet: {empty.map((group) => group.name).join(', ')}</span>
                  <span aria-hidden="true" className="text-accent group-open/empty:hidden">
                    Show
                  </span>
                  <span aria-hidden="true" className="hidden text-accent group-open/empty:inline">
                    Hide
                  </span>
                </summary>
                <div className="mt-3 space-y-5">{empty.map((group) => group.body)}</div>
              </details>
            )}
          </div>

          {/* From `md` the only thing the footer holds is Clear, so with no
              filter on there is no footer. On a phone Done is always there: a
              sheet needs a way out at the thumb, not only the button at the top
              of the screen that opened it. */}
          <div
            className={`flex items-center gap-3 border-t border-black/5 px-4 pb-[max(0.625rem,env(safe-area-inset-bottom))] pt-2.5 md:pb-2.5 ${
              active > 0 ? '' : 'md:hidden'
            }`}
          >
            {active > 0 && (
              <button
                type="button"
                data-testid="winery-filters-clear"
                // `EMPTY_WINERY_FILTERS` rather than a literal: a literal is a
                // second declaration of the filter set, and the one thing it
                // always forgets is the field a later phase added — which reads
                // as "Clear filters" leaving a filter on.
                onClick={() => onChange(EMPTY_WINERY_FILTERS)}
                className={`min-h-11 rounded text-sm font-medium text-accent underline decoration-accent/30 underline-offset-2 hover:decoration-accent md:min-h-9 ${CHROME_FOCUS_CLASS}`}
              >
                Clear filters
              </button>
            )}
            <button type="button" onClick={close} className={`ml-auto md:hidden ${CHROME_SHEET_DONE_CLASS}`}>
              Done
            </button>
          </div>
        </div>
      )}
    </>
  )
}

interface VisitingFilterProps {
  counts: Record<string, number>
  selected: string[]
  onChange: (visiting: string[]) => void
}

/**
 * How a visitor gets in. One shortcut on top — invitation and appointment
 * together, the visits worth planning a day around — and every door policy
 * below it, so the shortcut is a convenience over the same checkboxes rather
 * than a second filter that could disagree with them.
 *
 * `unknown` is counted but not offered: "wineries whose door policy nobody has
 * researched" is not a place anyone wants to go, and the count still says how
 * much of the atlas the ledger reaches.
 */
function VisitingFilter({ counts, selected, onChange }: VisitingFilterProps) {
  const special: string[] = [...WINERY_VISITING_SPECIAL]
  const specialCount = special.reduce((sum, value) => sum + (counts[value] ?? 0), 0)
  const specialOn = special.every((value) => selected.includes(value))
  const unknown = counts[WINERY_UNKNOWN] ?? 0

  const toggleSpecial = (on: boolean) =>
    onChange(on ? [...new Set([...selected, ...special])] : selected.filter((value) => !special.includes(value)))

  return (
    <fieldset data-testid="winery-visiting">
      <legend className={LEGEND_CLASS}>Visiting</legend>

      {/* A CHIP, above the rows it is a shortcut over: outlined in the accent,
          and filled with it when pressed. It was a full-width tinted bar, the
          size and place of a row, and so read as one more row that happened to
          be selected. A chip is a different kind of thing from a checkbox
          before it is touched, which is what the one button on this surface
          has to be. */}
      <button
        type="button"
        aria-pressed={specialOn}
        data-testid="winery-visiting-special"
        disabled={specialCount === 0}
        onClick={() => toggleSpecial(!specialOn)}
        className={`mb-1.5 mt-2 inline-flex min-h-11 max-w-full items-center gap-2 rounded-full border px-3.5 text-left text-[13px] transition-colors duration-100 disabled:cursor-not-allowed disabled:opacity-50 md:min-h-8 ${CHROME_FOCUS_CLASS} ${
          specialOn ? 'border-accent bg-accent text-white' : 'border-accent/40 text-accent hover:bg-accent/5'
        }`}
      >
        <span className="font-medium">Invite-only, members &amp; appointment</span>{' '}
        <span className={`text-xs tabular-nums ${specialOn ? 'text-white/80' : 'text-ink-soft'}`}>
          {number.format(specialCount)}
        </span>
      </button>

      <Choices
        name="winery-visiting"
        options={WINERY_VISITING_OPTIONS.map((value) => ({
          value,
          label: WINERY_VISITING_LABELS[value] ?? value,
          count: counts[value] ?? 0,
          checked: selected.includes(value),
          onChange: (on) => onChange(toggled(selected, value, on)),
        }))}
      />

      <p data-testid="winery-visiting-note" className={`${NOTE_CLASS} ml-[26px] mt-1`}>
        Every door policy is quoted from the winery&rsquo;s own page.
        {unknown > 0 && ` ${number.format(unknown)} producers have none recorded yet.`}
      </p>
    </fieldset>
  )
}

interface ChoiceOption {
  value: string
  label: string
  count: number
  checked: boolean
  onChange: (on: boolean) => void
}

// The note a row carries, or none: the reason it is empty, or what its rows
// cover where the coverage is uneven enough to mislead.
const noteFor = (option: ChoiceOption) => (option.count === 0 ? reasonFor(option.value) : coverageFor(option.value))

/**
 * One fieldset's run of rows.
 *
 * It exists for one decision a row cannot make alone: **a note repeated down
 * consecutive rows is SHOWN once, under the last of them.** The four size
 * classes share one reason for being empty, a sentence that wraps to four lines
 * at this width, so an atlas with no size data printed it four times and pushed
 * every other filter off the surface. Each row still owns its own `<p id>` and
 * its own `aria-describedby` — the earlier copies are `sr-only`, not removed —
 * because a screen reader lands on one checkbox at a time and "this is off, and
 * here is why" has to be reachable from each of them.
 */
function Choices({ name, options }: { name: string; options: ChoiceOption[] }) {
  const notes = options.map(noteFor)

  return (
    <>
      {options.map((option, index) => (
        <Choice
          key={option.value}
          name={name}
          option={option}
          note={notes[index]}
          repeatedBelow={notes[index] !== undefined && notes[index] === notes[index + 1]}
        />
      ))}
    </>
  )
}

interface ChoiceProps {
  name: string
  option: ChoiceOption
  note: string | undefined
  /** The next row says the same thing, so this copy is for screen readers only. */
  repeatedBelow: boolean
}

// One row, two rules that are the same rule in two states. A bucket with no rows
// is disabled and says which phase fills it; a bucket that HAS rows says what
// those rows cover, where the coverage is uneven enough to mislead. Both notes
// are wired with `aria-describedby` rather than left as nearby text: an
// explanation a screen reader does not reach is the enabled-but-inert failure in
// another form, and that applies just as much to "this filter works, but only
// in France" as to "this filter is off".
function Choice({ name, option, note, repeatedBelow }: ChoiceProps) {
  const { value, label, count, checked, onChange } = option
  const empty = count === 0
  const noteId = `${name}-${value}-${empty ? 'reason' : 'coverage'}`

  return (
    <div>
      <label className={ROW_CLASS}>
        <input
          type="checkbox"
          className={CHROME_CHECKBOX_CLASS}
          disabled={empty}
          checked={checked}
          aria-describedby={note ? noteId : undefined}
          onChange={(event) => onChange(event.target.checked)}
        />
        {/* The space between the two spans is a real text node and has to stay
            one. A flex row does not draw it, but it is what makes the control's
            accessible name "Unknown 16,800" rather than "Unknown16,800". */}
        <span className="flex-1">{label}</span>{' '}
        <span className={COUNT_CLASS}>{number.format(count)}</span>
      </label>
      {note && empty && (
        <p
          id={noteId}
          data-testid={noteId}
          className={repeatedBelow ? 'sr-only' : `${NOTE_CLASS} mb-1 ml-[26px] mt-0.5`}
        >
          {note}
        </p>
      )}
      {/* What an ENABLED row covers is a paragraph, and one a reader wants
          once, not under their eye every time: behind a disclosure. The
          paragraph is the checkbox's description whether or not it is open —
          a description is read from the document, not from the screen. */}
      {note && !empty && (
        <details className="mb-1 ml-[26px]">
          <summary
            className={`inline-flex min-h-11 cursor-pointer list-none items-center rounded text-xs text-accent underline decoration-accent/30 underline-offset-2 hover:decoration-accent md:min-h-6 [&::-webkit-details-marker]:hidden ${CHROME_FOCUS_CLASS}`}
          >
            What this covers
          </summary>
          <p id={noteId} data-testid={noteId} className={`${NOTE_CLASS} mt-0.5`}>
            {note}
          </p>
        </details>
      )}
    </div>
  )
}
